import React, { useEffect, useState } from "react";
import config from "clientappsetting.json";
import "./ServiceCentreSearchStyles.scss";
import * as yup from "yup";
import { Button, Modal } from "react-bootstrap";
import { ErrorMessage, Form as FormikForm, Formik, FormikErrors } from "formik";
import { Hero } from "components/generic/Hero";
import { RadioButtonProps } from "components/generic/RadioButton";
import { LoadingBar } from "components/LoadingBar";
import { ServiceCentreDetailsCard } from "components/ServiceCentreDetailsCard";
import { AnyTruthyProperty, assertNotNullOrUndefined, ConvertToSNServiceIds } from "helpers/Helpers";
import { INewBookingPropsBase } from "types/INewBookingPropsBase";
import { ServiceCentreListingRow } from "components/ServiceCentreListingRow/ServiceCentreListingRow";
import { scrollToTop } from "helpers/ScrollingHelper";
import { AdditionalServiceType, ServiceCentreCardDetails, ServiceCentreSearchRequest, SiteTypeFilter, WorkType } from "generated/types/types";
import { WorkDetails } from "types/NewBooking";
import { useWindowDimensions } from "hooks/WindowDimensionsHook";
import { ServiceCentreSearchActions } from "components/ServiceCentreSearchActions/ServiceCentreSearchActions";
import { StepId } from "logic/Navigation/Steps";
import { useTranslation } from "react-i18next";
import AddressSearch, {AddressSearchProps, DetailSuggestion, makeDefaultStrings, } from "components/AddressSearch";
import { mapLoqateGroupsGeneric } from "components/AddressSearch/utils/mappers";
import { stripLeadingDuplicate, stripTrailingCount } from "components/AddressSearch/utils/format";
import { LoqateConfig } from "./LoqateConfig";
import { Api } from "api/Base/Api";
import { CommandDetails } from "api/Base/CommandDetails";
import { useSelector } from "react-redux";
import { RootState } from "redux/store/store";

// This has to align with the medium breakpoint value that can be found in variables.scss
const mobileBreakpoint = 992;
import { ApiUrlResolver } from "utils/ApiUrlResolver";

type ServiceCentreSelectionFormValue = {
    selectedServiceCentre: string;
};

type SearchFormValues = {
    postcodeOrTown: string;
    siteName: string;
    searchRadius: number | "";
    siteTypeRestriction: SiteTypeFilter;
    isMainDealer: boolean;
};

interface Props extends INewBookingPropsBase {
    micrositePin: string;
    goToPreviousStep: () => void;
    vehicleRegNo: string;
    odometerReading: string | null;
    workDetails: WorkDetails;
    setSelectedServiceCentre: (value: ServiceCentreCardDetails) => void;
    maxGarageSearchRadius: number;
    showServiceCenterNameGarageSearchFilter: boolean;
    showSiteTypeGarageSearchFilter: boolean;
    showSearchRadiusGarageSearchFilter: boolean;
    showMainDealerGarageSearchFilter: boolean;
    displayNetworkPreferenceLevels: boolean;
    displayCollectionDistance: boolean;
    hideSitesOutsideCollectionRadius: boolean;
    showHiddenSites: boolean;
    previousStep: StepId;
    safetyRecall: boolean;
    setWorkDetails: (value: WorkDetails) => void;
}

export const ServiceCentreSearch: React.FC<Props> = ({ ...props }: Props) => {
const DEBOUNCE_MS =
    (Number(config?.ADDRESS_SEARCH?.DEBOUNCE_MS) || 0) > 0
    ? Number(config.ADDRESS_SEARCH.DEBOUNCE_MS)
    : 300;

const MIN_CHARS =
    (Number(config?.ADDRESS_SEARCH?.MIN_CHARS) || 0) > 0
    ? Number(config.ADDRESS_SEARCH.MIN_CHARS)
    : 2;
    const windowDimensions = useWindowDimensions();
    const [serviceCentreDetails, setServiceCentreDetails] = useState<ServiceCentreCardDetails[] | null>(null);
    const [serviceCentreCount, setServiceCentreCount] = useState<number>(5);
    const [serviceCentreOptions, setServiceCentreOptions] = useState<RadioButtonProps[]>([]);
    const [searchInProgress, setSearchInProgress] = useState<boolean>(false);
    // This will hold the values of the search results (sans filtering)
    const [searchResults, setSearchResults] = useState<ServiceCentreCardDetails[] | null>(null);
    const [isFilterOpened, setIsFilterOpened] = useState<boolean>(false);
    const [showPostcodePopup, setShowPostcodePopup] = useState<boolean>(false);
    const { t } = useTranslation();
    const countryCode = useSelector((state: RootState) => state.country.countryCode);
    const [selectedSearchAddress, setSelectedSearchAddress] = useState<string>("");
    const [filteredResults, setFilteredResults] = useState<ServiceCentreCardDetails[] | null>(null);
    const [allowAddressDropdown, setAllowAddressDropdown] = useState<boolean>(true);

    const serviceCentreSelectionSchema = yup.object({
        selectedServiceCentre: yup.string().required(t("service_center_search_listed_service_center")),
    });

    const searchFormSchema = yup.object({
        postcodeOrTown: yup.string().required(t("service_center_search_postcode_or_town")).max(150, t("service_center_search_postcode_150_char")),
        siteName: yup.string().nullable().max(150, t("service_center_search_site_150_char")),
        searchRadius: yup.number().nullable().max(500, t("service_center_search_500_miles")),
    });

    const getSessionCollectionPostcode = () =>
        sessionStorage.getItem("tempCollectionPostcode")
        ?? props.workDetails.collectionPostCode
        ?? "";

    const getInitialPostcode = () => {
        const isDelivery = props.workDetails.additionalServiceRequested?.includes("Delivery");
        if (props.previousStep === StepId.RequiredWork && isDelivery) {
            return getSessionCollectionPostcode();  
        }
        return "";
    }

    const initValue: SearchFormValues = {
        postcodeOrTown: getInitialPostcode(),
        siteName: sessionStorage.getItem("siteName") || "",
        siteTypeRestriction: (() => {
            const checkSiteType = sessionStorage.getItem("siteTypeRestriction");

            if (checkSiteType === "Mobile") {
                return SiteTypeFilter.Mobile
            }
            else if (checkSiteType === "Fixed") {
                return SiteTypeFilter.Fixed
            }
            return SiteTypeFilter.All;
        })(),
        isMainDealer: sessionStorage.getItem("isMainDealer") === "true",
        searchRadius: Number(sessionStorage.getItem("searchRadius")) || ""
    };
    let currentReg = "";
    const tempReg = sessionStorage.getItem("tempReg") || "";

    useEffect(() => {
    const isDelivery = props.workDetails.additionalServiceRequested?.includes("Delivery");
        if (props.previousStep === StepId.RequiredWork && isDelivery) {
             const lockedPc =
                sessionStorage.getItem("tempCollectionPostcode") ??
                props.workDetails.collectionPostCode ??
                "";
            const lockedAddr =
                sessionStorage.getItem("tempCollectionAddress") ??
                ""; 
            setAddrQuery(lockedPc);
            setSelectedSearchAddress(lockedAddr);
        } else {
            setAddrQuery("");
            setSelectedSearchAddress("");
        }
    }, [
        props.previousStep,
        props.workDetails.additionalServiceRequested,
        props.workDetails.collectionPostCode,
        props.workDetails.collectionAddress,
    ]);


    useEffect(() => {
        scrollToTop();
    }, []);

    const [addrQuery, setAddrQuery] = useState<string>("");


  const callAddressLookupApi = (text: string, containerId?: string) => {
  const command: CommandDetails = new CommandDetails();
  let payload: any = {};

  if (
    config.ONELINK_LOCATION_SERVICE.ONELINK_LOCATION_SERVICE_INTEGRATION ===
    config.ONELINK_LOCATION_SERVICE.ONELINK_LOCATION_SERVICE_INTEGRATION_ENABLED
  ) {
    payload = { Container: containerId ?? "", Countries: countryCode === "DK" ? "DNK" : "GBR" };
    command.baseUrl = config.ONELINK_LOCATION_SERVICE.BASE_ADDRESS_CAPTURE_FROM_POSTCODE_URL + `/${text}?`;
  } else {
    payload = {
      Key: config.LOQATE_KEY,
      Text: text,
      Countries: LoqateConfig.Countries,
      Language: LoqateConfig.Language,
      Container: containerId ?? "",
      Limit: LoqateConfig.Limit,
    };
    command.baseUrl = LoqateConfig.BaseFindUrl;
  }

  command.payload = payload;
  return new Api(command).Get();
};

    // retrieve a single address detail → postcode
    const selectCollectionPostCode = async (id: string) => {
    const command: CommandDetails = new CommandDetails();
    let payload: any = {};

    if (
        config.ONELINK_LOCATION_SERVICE.ONELINK_LOCATION_SERVICE_INTEGRATION ===
        config.ONELINK_LOCATION_SERVICE.ONELINK_LOCATION_SERVICE_INTEGRATION_ENABLED
    ) {
        payload = { countryCode: countryCode === "DK" ? "DNK" : "GBR" };
        command.baseUrl = config.ONELINK_LOCATION_SERVICE.BASE_FIND_ADDRESS_URL + `/${id}?`;
    } else {
        payload = { Key: config.LOQATE_KEY, Id: id };
        command.baseUrl = LoqateConfig.BaseRetrieveUrl;
    }

    command.payload = payload;
        const res = await new Api(command).Get();
        let postalCode = config.ONELINK_LOCATION_SERVICE.ONELINK_LOCATION_SERVICE_INTEGRATION ===
            config.ONELINK_LOCATION_SERVICE.ONELINK_LOCATION_SERVICE_INTEGRATION_ENABLED
            ? res.items[0].postalCode
            : res.Items[0].PostalCode;

        if (countryCode == "DK") {
            postalCode = res.items[0].postalCode + " " + res.items[0].city;
        }
        return postalCode;
    };

    const DIRECT_GROUP_ID = "__DIRECT_ADDR__";

    const fetchGroupSuggestions: AddressSearchProps["fetchGroupSuggestions"] = async (q) => {
        if (!q?.trim()) return [];
        const res = await callAddressLookupApi(q);

        const items = (res?.Items ?? []) as Array<{
            Id: string;
            Text: string;
            Description: string;
            Type: string;
        }>;

        const isAddr = (t: string) => LoqateConfig.isAnAddress(t);

        const addressHits = items.filter(i => isAddr(i.Type));
        const nonAddressItems = items.filter(i => !isAddr(i.Type));

        const rawGroups = mapLoqateGroupsGeneric(nonAddressItems, {
            formatDescription: LoqateConfig.formatDescription,
            isAddressType: LoqateConfig.isAnAddress,
        });

        const groups = rawGroups.map(g => ({
            ...g,
            label: stripTrailingCount(stripLeadingDuplicate(g.code, g.label)),
        }));

        if (groups.length > 0) {
            return groups;
        }

        if (addressHits.length > 0) {
            return [{
            id: DIRECT_GROUP_ID,
            code: "",
            label: "Results", 
            count: addressHits.length,
            }];
        }

        return [];
    };

    const fetchDetailSuggestions: AddressSearchProps["fetchDetailSuggestions"] = async (groupId) => {
        const res = await callAddressLookupApi(addrQuery, groupId === DIRECT_GROUP_ID ? undefined : groupId);

        const items = (res?.Items ?? []) as Array<{
            Id: string;
            Text: string;
            Description: string;
            Type: string;
        }>;

        const detailsSource = groupId === DIRECT_GROUP_ID
            ? items.filter(i => LoqateConfig.isAnAddress(i.Type))
            : items.filter(i => LoqateConfig.isAnAddress(i.Type));

        return detailsSource.map<DetailSuggestion>((i) => ({
            id: i.Id,
            label: `${i.Text} ${LoqateConfig.formatDescription(i.Description)}`.trim(),
        }));
    };

    const handleShowHiddenSites = () => {
        const temp = [...(searchResults || [])];
        setServiceCentreOptions(constructServiceCentreOptions(temp));
        setServiceCentreDetails(temp);
    }

    const getCollectionPostcode = () =>
        (sessionStorage.getItem("tempCollectionPostcode") ||
            props.workDetails.collectionPostCode ||
            ""
        ).trim().toUpperCase();

    const getCollectionAddress = () =>
        sessionStorage.getItem("tempCollectionAddress") ||
        props.workDetails.collectionAddress ||
        "";

    const executeSearchRequest = (searchValues: SearchFormValues) => {
        const isDelivery = props.workDetails.additionalServiceRequested?.includes("Delivery") === true;
        const usedPostcode = (searchValues.postcodeOrTown || "").trim().toUpperCase();

        const collectionPostcode = getCollectionPostcode();
        const collectionAddress = getCollectionAddress();

        const requestData: ServiceCentreSearchRequest = {
            vehicleRegNo: props.vehicleRegNo,
            odometerReading: props.odometerReading || "",
            driverMicrositePin: props.micrositePin,
            postcodeOrTown: usedPostcode,
            requiredServices: ConvertToSNServiceIds(
                props.workDetails.additionalServiceRequested,
                props.workDetails.workRequired
            ),
            pageNo: 1,
            resultsPerPage: Number(config.SITE_SEARCH_PAGE_SIZE),
            searchRadiusMiles: props.maxGarageSearchRadius,
            collectionPostCode: isDelivery && collectionPostcode ? collectionPostcode : undefined,
            hideSitesOutsideCollectionRadius: isDelivery ? props.hideSitesOutsideCollectionRadius : false,
            showHiddenSites: isDelivery ? props.showHiddenSites : true,
            safetyRecall: props.workDetails.safetyRecall,
            searchAddress: isDelivery && collectionAddress ? collectionAddress : selectedSearchAddress,
        };

        currentReg = props.vehicleRegNo;
        const requestDataString = JSON.stringify(requestData);
        console.log(requestDataString);
        const apiUrl = ApiUrlResolver.getWebApiUrl(countryCode);
        fetch(apiUrl + "/ServiceCentre/GetServiceCentres", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: requestDataString,
        })
            .then(response => response.json())
            .then(data => {

                let filteredData = data;
                console.log("props.hideSitesOutsideCollectionRadius:", props.hideSitesOutsideCollectionRadius);

                if (isDelivery && props.hideSitesOutsideCollectionRadius) {
                    filteredData = data.filter((centre: ServiceCentreCardDetails) => {
                        
                        console.log("centre.collectionDeliveryRadius:", centre.collectionDeliveryRadius);
                        console.log("centre.distanceToCollectionDelivery:", centre.distanceToCollectionDelivery);

                        if (!centre.isCollectionDelivery) return true;

                        const radius = centre.collectionDeliveryRadius;
                        const distToCollection = centre.distanceToCollectionDelivery;

                       if (
                            radius === null ||
                            radius === undefined ||
                            distToCollection === null ||
                            distToCollection === undefined
                        ) {
                            return true; // fail open
                        }

                        return radius >= distToCollection;

                    });
                }
                
                setFilteredResults(filteredData);
                setServiceCentreOptions(constructServiceCentreOptions(filteredData));
                setServiceCentreDetails(filteredData);
                setSearchResults(data);
                setSearchInProgress(false);
            });
    };

    const constructServiceCentreOptions = (data: ServiceCentreCardDetails[]): RadioButtonProps[] => {
        return data.map(mapServiceCentreResponse);
    };

    const isDelivery = props.workDetails.additionalServiceRequested?.includes("Delivery");

    const mapServiceCentreResponse = (data: ServiceCentreCardDetails): RadioButtonProps => {
        const distanceLabel = countryCode == "DK" ? t("service_center_search_km") : t("service_center_search_miles");
        const leadTimeLabel = t("service_center_search_days");
        let radioButtonProps: RadioButtonProps = ({
            value: data.id,
            label: data.name,
            secondaryLabel: data.isHomeServiceCentre ? t("service_center_search_preferred_garage") : "",
            tertiaryLabel: t("service_center_search_distance_search") + data.distance.toString() + distanceLabel,
            preferredSupplier: props.displayNetworkPreferenceLevels ? (data.preferredText || "") : "",
            leadTime: t("service_center_search_lead_time") + data.leadTime.toString() + leadTimeLabel
        });

        if (props.displayCollectionDistance && isDelivery) {
          
            radioButtonProps = {
                ...radioButtonProps,
                quaternaryLabel: data.isCollectionDelivery
                    ? t("service_center_search_distance_location") + data.distanceToCollectionDelivery + distanceLabel
                    : "",
                quaternaryClass: data.isCollectionDelivery
                    ? (data.collectionDeliveryRadius < data.distanceToCollectionDelivery
                        ? "text-danger"
                        : "text-success")
                    : "",
            };
        }

        return radioButtonProps;
    }
    const onSearchSubmit = (values: SearchFormValues, { setSubmitting }: { setSubmitting: (x: boolean) => void }) => {
       setAllowAddressDropdown(false);
       const isDelivery = workType === AdditionalServiceType.Delivery;
       const collection = (
            sessionStorage.getItem("tempCollectionPostcode") || 
            props.workDetails.collectionPostCode ||
             ""
        ).trim().toUpperCase();

        const entered = (values.postcodeOrTown || "").trim().toUpperCase();

        if (isDelivery && collection && collection !== entered) {
            setShowPostcodePopup(true);
            return;
        }
        handleSearch(values, { setSubmitting });
    }

    const handleSearch = (values: SearchFormValues, { setSubmitting }: { setSubmitting: (x: boolean) => void }) => {
        setServiceCentreCount(5);
        setSearchInProgress(true);
        executeSearchRequest(values);
        setSubmitting(false);

        if (tempReg.includes(currentReg)) {
            sessionStorage.setItem("tempPostCode", values.postcodeOrTown);
        }
    };

    const refineSearch = (
        values: SearchFormValues,
        { setSubmitting, validateForm }: { setSubmitting: (x: boolean) => void; validateForm: (values?: SearchFormValues) => Promise<FormikErrors<SearchFormValues>> }
    ) => {
        // Manually validate the form if it has any errors step out
        validateForm().then((x: FormikErrors<SearchFormValues>) => {
            // If there are any truthy values in the errors object it means we have validation errors and shouldn't continue
            if (AnyTruthyProperty(x)) {
                return;
            }

            sessionStorage.setItem("isMainDealer", JSON.stringify(values.isMainDealer));
            sessionStorage.setItem("siteName", values.siteName);
            sessionStorage.setItem("siteTypeRestriction", values.siteTypeRestriction);
            sessionStorage.setItem("searchRadius", JSON.stringify(values.searchRadius));

            // we don't need to contact the API can just refine locally
            if (searchResults !== null) {

                setSearchInProgress(true);
                setServiceCentreCount(5);

                const siteNameInput = values.siteName.toLowerCase();
                const searchRadiusInput = values.searchRadius;
                const siteTypeRestriction = values.siteTypeRestriction;
                const isMainDealer = values.isMainDealer;

                let temp = [...searchResults];

                if (siteNameInput) {
                    temp = temp.filter(x => x.name.toLowerCase().includes(siteNameInput));
                }

                if (searchRadiusInput) {
                    temp = temp.filter(x => x.distance <= (searchRadiusInput ? searchRadiusInput : 50));
                }

                if (siteTypeRestriction && siteTypeRestriction !== SiteTypeFilter.All) {
                    temp = temp.filter(x => x.isMobileServiceCentre === (siteTypeRestriction === SiteTypeFilter.Mobile));
                }

                if (isMainDealer) {
                    temp = temp.filter(x => x.isMainDealer === isMainDealer);
                }

                const options = constructServiceCentreOptions(temp);
                setServiceCentreOptions(options);
                setServiceCentreDetails(temp);
                setSubmitting(false);
                setSearchInProgress(false);
            } else {
                handleSearch(values, { setSubmitting });
            }
        });
    };

    const handleSubmit = (values: ServiceCentreSelectionFormValue) => {
        props.setSelectedServiceCentre(assertNotNullOrUndefined(serviceCentreDetails?.find(x => x.id === values.selectedServiceCentre)));
        props.goToNextStep();
    };

    const searchNewPostcode = (values: SearchFormValues, { setSubmitting }: { setSubmitting: (x: boolean) => void }) => {
        setShowPostcodePopup(false);
        handleSearch(values, { setSubmitting });
    }

    const searchCollectionPostcode = (values: SearchFormValues, { setSubmitting, setFieldValue }: any) => {
        setShowPostcodePopup(false);

        const pc = (sessionStorage.getItem("tempCollectionPostcode") ||
                    props.workDetails.collectionPostCode ||
                    "").trim().toUpperCase();
                    
        setFieldValue("postcodeOrTown", pc);
        setAddrQuery(pc);
        setSelectedSearchAddress(props.workDetails.collectionAddress || "");

        handleSearch({ ...values, postcodeOrTown: pc }, { setSubmitting });
    };

    const onPostcodePopupClosed = ({ setSubmitting }: { setSubmitting: (x: boolean) => void }) => {
        setShowPostcodePopup(false);
        setSubmitting(false);
    }

    const determineWorkType = (): AdditionalServiceType => {
        if (props.workDetails.additionalServiceRequested.includes("Courtesy")) {
            return AdditionalServiceType.Courtesy;
        }
        if (props.workDetails.additionalServiceRequested.includes("Delivery")) {
            return AdditionalServiceType.Delivery;
        }
        if (props.workDetails.additionalServiceRequested.includes("WhileUWait")) {
            return AdditionalServiceType.WhileUWait;
        }
        return AdditionalServiceType.NoAdditional;
    };
    const workType = determineWorkType();
    return (
        <div key="available-garages">
            <div className="row no-gutters">
                <div className="col-12">
                    <Hero heading={t("service_center_search_garage_search")} description={t("service_center_search_preferred_location")} />
                    <Formik
                        validateOnChange={false}
                        validateOnBlur={false}
                        onSubmit={onSearchSubmit}
                        initialValues={initValue}
                        validationSchema={searchFormSchema}
                        enableReinitialize={true}
                    >
                        {({ isSubmitting, values, setSubmitting, validateForm, setFieldValue, errors, submitCount }) => (
                            <FormikForm>
                                <div className="row g-3 align-items-end mb-3">
                                    <div className="col-12 col-lg-8">
                                        <div className="address-search-field mb-0">
                                        <label className="form-label">{t("address_search_type_search_location")}</label> 
                                        <AddressSearch
                                            id="garage-addr"
                                            value={addrQuery}
                                            onChange={(next) => {
                                                setAllowAddressDropdown(true);
                                                setAddrQuery(next);
                                                setFieldValue("postcodeOrTown", next);
                                            }}
                                            onClear={() => {
                                                setAllowAddressDropdown(true);
                                                setAddrQuery("");
                                                setFieldValue("postcodeOrTown", "");
                                            }}
                                            fetchGroupSuggestions={fetchGroupSuggestions}
                                            fetchDetailSuggestions={fetchDetailSuggestions}
                                            onSelectDetail={async (d) => {
                                                const pc = await selectCollectionPostCode(d.id);
                                                setAddrQuery(pc);
                                                setFieldValue("postcodeOrTown", pc);
                                                setSelectedSearchAddress(d.label);
                                            }}
                                            showSummaryCard={false}
                                            strings={ makeDefaultStrings(t)}
                                            keepPostcodeOnSelect
                                            debounceMs={DEBOUNCE_MS}
                                            minChars={MIN_CHARS}
                                            suppressFirstOpen
                                            allowDropdown={allowAddressDropdown}
                                        />

                                        {submitCount > 0 && errors.postcodeOrTown ? (
                                            <div className="error-msg">{errors.postcodeOrTown}</div>
                                        ) : null}
                                        </div>

                                    </div>
                                   <div className="col-12 col-lg-4 d-flex align-items-end">
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            className="large-input w-100 search-btn"
                                            disabled={isSubmitting || isFilterOpened || searchInProgress}
                                            >
                                            {t("service_center_search_search")}
                                        </Button>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-12">
                                     {(searchResults || []).length > 0 && (
                                        <ServiceCentreSearchActions
                                            goToPreviousStep={props.goToPreviousStep}
                                            refineOnClick={() => refineSearch(values, { setSubmitting, validateForm })}
                                            setFieldValue={setFieldValue}
                                            onFilterExpand={setIsFilterOpened}
                                            showServiceCenterNameGarageSearchFilter={props.showServiceCenterNameGarageSearchFilter}
                                            showSiteTypeGarageSearchFilter={props.showSiteTypeGarageSearchFilter}
                                            showSearchRadiusGarageSearchFilter={props.showSearchRadiusGarageSearchFilter}
                                            showMainDealerGarageSearchFilter={props.showMainDealerGarageSearchFilter}
                                            showHiddenSites={props.showHiddenSites}
                                            handleShowHiddenSites={handleShowHiddenSites}
                                            WorkType={workType}
                                            safetyRecall={props.workDetails.safetyRecall}
                                        />
                                        )}
                                    </div>
                                </div>

                                <Modal show={showPostcodePopup} onHide={() => onPostcodePopupClosed({ setSubmitting })} >
                                    <Modal.Body>
                                        <div className="custom-dialog">
                                            <h2>{t("service_center_search_postcode_search")}</h2>

                                            <Button size="lg" variant="primary" onClick={() => searchNewPostcode(values, { setSubmitting })}>
                                                {t("service_center_search_new_postcode")}
                                            </Button>

                                            <p className="link-muted" onClick={() => searchCollectionPostcode(values, { setSubmitting, setFieldValue })}>
                                                {t("service_center_search_collection_postcode")}
                                            </p>
                                        </div>

                                    </Modal.Body>
                                </Modal>
                            </FormikForm>
                        )}
                    </Formik>

                    {searchInProgress ? <LoadingBar /> : null}
                    {!searchInProgress && serviceCentreDetails && serviceCentreDetails.length <= 0 ? (
                        <div className="text-center my-3">
                            <p className="mb-0">{t("service_center_search_no_service_centers_found")}</p>
                        </div> ) : null}
                    {!searchInProgress && serviceCentreDetails && serviceCentreDetails?.length > 0 ? (
                        <Formik
                            enableReinitialize={true}
                            onSubmit={handleSubmit}
                            initialValues={{ selectedServiceCentre: serviceCentreDetails[0].id }}
                            validationSchema={serviceCentreSelectionSchema}
                        >
                            {({ values }) => (
                                <FormikForm>
                                    <div className="garage-selection-container">
                                        <div className="servicecentre-list">
                                            {serviceCentreOptions
                                                .filter(x => serviceCentreOptions.indexOf(x) < serviceCentreCount)
                                                .map(x => (
                                                    <ServiceCentreListingRow
                                                        key={x.value}
                                                        serviceCentreDetails={serviceCentreDetails.find(details => details.id === x.value)}
                                                        radioButtonProps={x}
                                                        selectedServiceCentreId={values.selectedServiceCentre}
                                                        windowWidth={windowDimensions.width}
                                                    />
                                                ))}
                                            {serviceCentreDetails.length > serviceCentreCount ? (
                                                <div
                                                    className="load-more-button"
                                                    onClick={() => {
                                                        setServiceCentreCount(serviceCentreCount + 5);
                                                    }}
                                                >
                                                    {t("service_center_search_load_more")}
                                                </div>
                                            ) :  null}

                                            <ErrorMessage name="selectedServiceCentre" component="div" className="error-msg" />
                                        </div>
                                        {/* This is the service centre card that will only be shown on large screens */}
                                        {windowDimensions.width > mobileBreakpoint ? (
                                            <div className="servicecentre-details">
                                            <ServiceCentreDetailsCard
                                                serviceCentreDetails={serviceCentreDetails.find(x => x.id === values.selectedServiceCentre)}
                                            />
                                            <Button type="submit" variant="primary" className="large-input">
                                                {t("service_center_search_continue")}
                                            </Button>
                                            </div>
                                        ) : null}
                                        </div>
                                    </FormikForm>
                                )}
                        </Formik>
                    ) : null}
                </div>
            </div>
        </div>
    );
};
